import { type ReactNode } from "react";

import { Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";

import { ActionIcon, Box, Button, Drawer, Flex, Group, Title, Tooltip } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { IconX } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import { BaselineFhrRefreshContext } from "@/encounters/components/EncounterLabor/contexts/BaselineFhrRefreshContext";
import { PostpartumVitalsQuestionnaireForm } from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/components/PostpartumVitalsQuestionnaireForm";
import { isPostpartumVitalsQuestionnaire } from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/utils/normalize-postpartum-vitals-questionnaire";
import { FormValues } from "@/encounters/components/EncounterLabor/types/labor";
import QuestionnaireForm from "@/intake-forms/components/QuestionnaireForm";
import { QuestionnaireUtcDateAnchorContext } from "@/intake-forms/components/QuestionnaireTimeField/QuestionnaireTimeField";

interface LaborQuestionnaireDrawerProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  submitButtonText: string;
  formId: string;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  isSubmitting: boolean;
  questionnaire?: Questionnaire | null;
  questionnaireResponse?: QuestionnaireResponse | null;
  form: UseFormReturnType<FormValues>;
  error: string | null;
  dateTimeUtcDateAnchors?: Record<string, string>;
  /** Optional fields shown above the questionnaire (e.g. labor stage during amend). */
  laborStageSection?: ReactNode;
  /** Bump after FHR save so BaselineFhr refetches while the drawer stays open. */
  baselineRefreshKey?: number;
}

export function LaborQuestionnaireDrawer({
  opened,
  onClose,
  title,
  submitButtonText,
  formId,
  onSubmit,
  isLoading,
  isSubmitting,
  questionnaire,
  questionnaireResponse,
  form,
  error,
  dateTimeUtcDateAnchors = {},
  laborStageSection,
  baselineRefreshKey = 0,
}: LaborQuestionnaireDrawerProps) {
  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSubmitting) {
      return;
    }
    onSubmit(e);
  };

  return (
    <Drawer
      size={500}
      opened={opened}
      position="right"
      onClose={onClose}
      closeOnEscape={false}
      withCloseButton={false}
      closeOnClickOutside={false}
      styles={{
        body: { padding: 0 },
        content: { background: "white" },
      }}
    >
      <form id={formId} onSubmit={handleSubmit} data-testid="labor-flowsheet-form">
        <Box
          px="xs"
          bg="gray.0"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
          }}
        >
          <Group p="xs" justify="space-between">
            <Group gap="xs">
              <ActionIcon
                c="dark.9"
                variant="subtle"
                onClick={handleClose}
                disabled={isSubmitting}
                data-testid="labor-flowsheet-drawer-close"
              >
                <Tooltip label="Close">
                  <IconX size={16} />
                </Tooltip>
              </ActionIcon>
              <Title order={4} c="dark.9">
                {title}
              </Title>
            </Group>
            <Button
              type="submit"
              form={formId}
              loading={isLoading || isSubmitting}
              disabled={isLoading || isSubmitting}
              data-testid="add-to-flowsheet-drawer-button"
            >
              {submitButtonText}
            </Button>
          </Group>
        </Box>
        {isLoading ? (
          <Loading />
        ) : error ? (
          <Box p="md" c="red">
            <Title order={5}>Could not load form</Title>
            <p>{error}</p>
          </Box>
        ) : !questionnaire ? (
          <Loading />
        ) : (
          <BaselineFhrRefreshContext.Provider value={{ refreshWhen: opened, baselineRefreshKey }}>
            <QuestionnaireUtcDateAnchorContext.Provider value={dateTimeUtcDateAnchors}>
              <Flex p="lg" gap="lg" direction="column">
                {laborStageSection}
                {isPostpartumVitalsQuestionnaire(questionnaire) ? (
                  <PostpartumVitalsQuestionnaireForm form={form} questionnaire={questionnaire} />
                ) : (
                  <QuestionnaireForm
                    form={form}
                    group={questionnaire?.item?.[0]}
                    questionnaireResponse={{
                      linkId: questionnaireResponse?.item?.[0]?.linkId || "",
                    }}
                  />
                )}
              </Flex>
            </QuestionnaireUtcDateAnchorContext.Provider>
          </BaselineFhrRefreshContext.Provider>
        )}
      </form>
    </Drawer>
  );
}
